import {toast} from '@wandb/weave/common/components/elements/Toast';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {isEqual, omit} from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useHistory} from 'react-router';

import {
  useRegistryOrganizationQuery,
  useRegistryProjectMembersQuery,
  useRenameProjectMutation,
} from '../../../generated/graphql';
import {useIsRestrictedAccessOptionEnabled} from '../../../pages/HomePage/HomePageSidebar/useIsRestrictedAccessOptionEnabled';
import {Analytics} from '../../../services/analytics';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import {AccessOption} from '../../../util/permissions';
import * as urls from '../../../util/urls';
import {TextArea} from '../../Form/TextArea';
import {ArtifactTypeInfoBanner} from '../common/ArtifactTypeInfoBanner';
import {ArtifactTypeMultiInput} from '../common/ArtifactTypeMultiInput';
import {RegistryAccessOptionType} from '../common/permissionUtils';
import {RegistriesSearchContext} from '../common/RegistriesSearchContextProvider';
import {RegistryAccessOptions} from '../common/RegistryAccessOptions';
import {RegistryArtifactTypesOptions} from '../common/RegistryArtifactTypesOptions';
import {
  DEFAULT_REGISTRY_PROJECT_TYPES,
  globalRegistryType,
  REGISTRY_PROJECT_PREFIX,
} from '../common/utils';
import {REGISTRY_PROJECT_MEMBERS_QUERY} from '../graphql/registryProjectMembers.query';
import {FETCH_REGISTRY_PROJECTS_PARTIAL} from '../graphql/registryProjects.query';
import {FETCH_REGISTRIES_PAGE_SIZE} from '../RegistriesHomepage/hooks';
import {DEFAULT_ACCESS_OPTIONS} from '../workflows/CreateRegistryDrawer/CreateRegistryDrawerInner';
import {useUpsertRegistryProjectWithTypes} from '../workflows/CreateRegistryDrawer/hooks';
import {ConfirmRegistryAccessModal} from './ConfirmRegistryAccessModal';

// Helper function that returns whether the selected allowed-type setting is valid
// It is invalid if the user wants to allow only certain types, but the
// registry project's type-list and the user's input list is empty.
// If we allow this invalid setting, then the registry could end up in a state
// where no collections are allowed to be created since no types would be allowed in the registry.
const getSelectedTypeSettingIsInvalid = (
  allowAllArtifactTypesInRegistry: boolean,
  inputArtifactTypesList: string[],
  currentRegistryTypesList: string[]
) =>
  !allowAllArtifactTypesInRegistry &&
  inputArtifactTypesList.length === 0 &&
  currentRegistryTypesList.length === 0;

type RegistryDetailsProps = {
  currentRegistry: globalRegistryType;
};
export const RegistryDetails = ({currentRegistry}: RegistryDetailsProps) => {
  const isDefaultRegistry = DEFAULT_REGISTRY_PROJECT_TYPES.includes(
    currentRegistry.registryName
  );
  const history = useHistory();
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [newRegistry, setNewRegistry] = useState(currentRegistry);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [inputArtifactTypes, setInputArtifactTypes] = useState<string[]>([]);

  const {upsertRegistryProjectWithTypes, isLoading: isUpsertLoading} =
    useUpsertRegistryProjectWithTypes(currentRegistry.orgName);
  const {refetch: refetchRegistryMembers} = useRegistryProjectMembersQuery({
    variables: {
      entityName: currentRegistry.entityName,
      projectName: currentRegistry.registryProjectName,
    },
    context: propagateErrorsContext(),
  });
  const {searchPattern} = useContext(RegistriesSearchContext);
  const [renameProject, {loading: isRenameLoading}] = useRenameProjectMutation({
    refetchQueries: [
      {
        query: FETCH_REGISTRY_PROJECTS_PARTIAL,
        variables: {
          orgName: currentRegistry.orgName,
          limit: FETCH_REGISTRIES_PAGE_SIZE,
          pattern: searchPattern,
        },
      },
      {
        query: REGISTRY_PROJECT_MEMBERS_QUERY,
        variables: {
          entityName: currentRegistry.entityName,
          projectName: currentRegistry.registryProjectName,
        },
      },
    ],
    // Adding this so that the 'then' happens after the refetch
    awaitRefetchQueries: true,
  });

  const handleRegistryNameInputChange = useCallback(
    (value: string) => {
      const invalidUrlChars = '/\\#?%:'.split('');
      if (invalidUrlChars.some(c => value.includes(c))) {
        return;
      }
      setNewRegistry({
        ...newRegistry,
        registryName: value,
        registryProjectName: `${REGISTRY_PROJECT_PREFIX}${value.trim()}`,
      });
    },
    [newRegistry]
  );
  const handleOnBlurUpsert = useCallback(async () => {
    if (
      getSelectedTypeSettingIsInvalid(
        newRegistry.allowAllArtifactTypesInRegistry,
        inputArtifactTypes,
        currentRegistry.types ?? []
      )
    ) {
      return Promise.resolve();
    }

    // We remove the owner from this check for 2 reasons:
    // 1. The owner is not being edited in this workflow
    // 2. The owner's photoURL is a time-sensitive signed URL so 2
    // registries may often differ even without having made a valid UI change
    const sanitize = (registry: globalRegistryType) =>
      omit(registry, ['owner']);
    if (
      !isEqual(sanitize(currentRegistry), sanitize(newRegistry)) ||
      inputArtifactTypes.length > 0
    ) {
      await upsertRegistryProjectWithTypes({
        upsertAction: 'Edit',
        description: newRegistry.description,
        registryProjectName: currentRegistry.registryProjectName,
        entityName: currentRegistry.entityName,
        access: newRegistry.access,
        inputArtifactTypes,
        allowAllArtifactTypesInRegistry:
          newRegistry.allowAllArtifactTypesInRegistry,
      }).then(() => setInputArtifactTypes([]));
    }
  }, [
    newRegistry,
    currentRegistry,
    inputArtifactTypes,
    upsertRegistryProjectWithTypes,
  ]);

  const handleOnBlurRename = useCallback(async () => {
    // Trim both names for comparison to handle existing registries with trailing spaces
    if (
      newRegistry.registryName.trim() !== currentRegistry.registryName.trim()
    ) {
      try {
        await renameProject({
          variables: {
            entityName: currentRegistry.entityName,
            oldProjectName: currentRegistry.registryProjectName,
            newProjectName: newRegistry.registryProjectName,
          },
          context: propagateErrorsContext(),
        }).then(() => {
          toast('Updated registry settings!');
          history.push(
            urls.globalRegistry(
              newRegistry.orgName,
              newRegistry.registryName.trim(),
              undefined,
              undefined,
              undefined,
              undefined,
              true
            )
          );
        });
      } catch (e) {
        const msg = extractErrorMessageFromApolloError(e);
        if (msg) {
          toast(msg);
        }
      }
    }
  }, [
    newRegistry.registryName,
    currentRegistry.registryName,
    currentRegistry.entityName,
    currentRegistry.registryProjectName,
    newRegistry.registryProjectName,
    newRegistry.orgName,
    renameProject,
    history,
  ]);

  const onUpdateInputArtifactTypes = useCallback(
    (types: string[]) => {
      setInputArtifactTypes(types);
      setNewRegistry({
        ...newRegistry,
        types: [...(currentRegistry.types ?? []), ...types],
      });
    },
    [currentRegistry.types, newRegistry, setInputArtifactTypes, setNewRegistry]
  );

  const {data: organizationData, loading: isOrganizationQueryLoading} =
    useRegistryOrganizationQuery({
      variables: {orgName: currentRegistry.orgName},
      context: propagateErrorsContext(),
      skip: isUpsertLoading || isRenameLoading,
    });
  const orgId = organizationData?.organization?.id ?? '';

  const {isRestrictedOptionEnabled} = useIsRestrictedAccessOptionEnabled(orgId);
  const accessOptions = useMemo(() => {
    const registryAccessOptions: RegistryAccessOptionType[] = [];
    registryAccessOptions.push(...DEFAULT_ACCESS_OPTIONS);
    return registryAccessOptions;
  }, []);

  useEffect(
    () =>
      setIsFormDisabled(
        isUpsertLoading || isRenameLoading || isOrganizationQueryLoading
      ),
    [isUpsertLoading, isRenameLoading, isOrganizationQueryLoading]
  );

  return (
    <Tailwind>
      <div className="my-24 flex w-full flex-col rounded-lg border border-solid border-moon-250 bg-white px-32 pb-32 pt-24">
        {!isDefaultRegistry && (
          <>
            <div className="mb-4 flex w-full items-center">
              <div className="text-base font-semibold leading-8">Name</div>
            </div>
            <div className="mb-16 max-w-[552px] text-base font-normal">
              <TextField
                placeholder="Name this registry"
                value={newRegistry.registryName ?? ''}
                onChange={handleRegistryNameInputChange}
                onBlur={handleOnBlurRename}
                autoFocus={true}
                disabled={isFormDisabled}
              />
            </div>
          </>
        )}
        <div className="mb-4 flex w-full items-center">
          <div className="text-base font-semibold leading-8">
            Description{' '}
            <span className="text-base font-normal italic leading-8 text-moon-500">
              (optional)
            </span>
          </div>
        </div>
        <div className="mb-8 max-w-[552px] text-base font-normal">
          <TextArea
            placeholder="Describe this registry's use case"
            value={newRegistry.description ?? ''}
            onChange={event => {
              setNewRegistry({...newRegistry, description: event.target.value});
            }}
            onBlur={handleOnBlurUpsert}
            disabled={isFormDisabled || isDefaultRegistry}
          />
        </div>
        <div className="mb-4 flex w-full items-center">
          <div
            data-test="registry-visibility-header"
            className="text-base font-semibold leading-8">
            Registry visibility
          </div>
        </div>
        <div
          className="mb-16 max-w-[552px] text-base font-normal"
          data-test="registry-visibility-select">
          <RegistryAccessOptions
            isRestrictedProjectEnabled={isRestrictedOptionEnabled ?? false}
            selectedRegistryAccess={newRegistry.access}
            setSelectedRegistryAccess={value => {
              setNewRegistry({...newRegistry, access: value});
              if (
                currentRegistry.access !== value &&
                value === AccessOption.Restricted
              ) {
                setIsAccessModalOpen(true);
              }
            }}
            onBlur={async () => {
              if (
                currentRegistry.access !== newRegistry.access &&
                newRegistry.access === AccessOption.Private
              ) {
                await handleOnBlurUpsert();
                refetchRegistryMembers?.();
                Analytics.track('Registry interaction', {
                  action: 'update visibility',
                  entityName: currentRegistry.entityName,
                  registryProjectName: currentRegistry.registryProjectName,
                  registryName: currentRegistry.registryName,
                });
              }
            }}
            registryAccessOptions={accessOptions}
            disabled={isDefaultRegistry || isFormDisabled}
          />
        </div>
        <div className="mb-4 flex w-full items-center">
          <div
            data-test="accepted-artifact-types-header"
            className="text-base font-semibold leading-8">
            Accepted artifact types
          </div>
          <Tooltip
            trigger={<Icon name="info" style={{marginLeft: '8px'}} />}
            content="Define the accepted artifact types for the registry."
          />
        </div>
        <div className="max-w-[552px] text-base font-normal">
          <RegistryArtifactTypesOptions
            selectedArtifactTypeConfig={
              newRegistry.allowAllArtifactTypesInRegistry
            }
            setSelectedArtifactTypeConfig={(allowAllTypes: boolean) => {
              setNewRegistry({
                ...newRegistry,
                allowAllArtifactTypesInRegistry: allowAllTypes,
              });
            }}
            onBlur={async () => {
              if (newRegistry.allowAllArtifactTypesInRegistry) {
                await handleOnBlurUpsert();
              }
              Analytics.track('Registry interaction', {
                action: 'update to allow all types',
                entityName: currentRegistry.entityName,
                registryProjectName: currentRegistry.registryProjectName,
                registryName: currentRegistry.registryName,
              });
            }}
          />
          {!newRegistry.allowAllArtifactTypesInRegistry && (
            <div className="mt-20">
              <ArtifactTypeMultiInput
                size="medium"
                existingArtifactTypes={currentRegistry.types ?? []}
                inputArtifactTypes={inputArtifactTypes}
                setInputArtifactTypes={onUpdateInputArtifactTypes}
                onBlur={async () => {
                  if (
                    getSelectedTypeSettingIsInvalid(
                      newRegistry.allowAllArtifactTypesInRegistry,
                      inputArtifactTypes,
                      currentRegistry.types ?? []
                    )
                  ) {
                    setNewRegistry({
                      ...newRegistry,
                      allowAllArtifactTypesInRegistry: true,
                    });
                    toast('Please add types that you would like to allow');
                  } else if (inputArtifactTypes.length > 0) {
                    Analytics.track('Registry interaction', {
                      action: 'update types list',
                      entityName: currentRegistry.entityName,
                      registryProjectName: currentRegistry.registryProjectName,
                      registryName: currentRegistry.registryName,
                    });
                  }
                  await handleOnBlurUpsert();
                }}
                autoFocus={
                  currentRegistry.allowAllArtifactTypesInRegistry &&
                  !newRegistry.allowAllArtifactTypesInRegistry
                }
              />
            </div>
          )}
        </div>
        {inputArtifactTypes.length > 0 && <ArtifactTypeInfoBanner />}
      </div>
      <ConfirmRegistryAccessModal
        isOpen={isAccessModalOpen}
        setIsModalOpen={setIsAccessModalOpen}
        onClickConfirm={async () => {
          await handleOnBlurUpsert();
          Analytics.track('Registry interaction', {
            action: 'update visibility',
            entityName: currentRegistry.entityName,
            registryProjectName: currentRegistry.registryProjectName,
            registryName: currentRegistry.registryName,
          });
          setIsAccessModalOpen(false);
          refetchRegistryMembers?.();
        }}
        onClickCancel={() =>
          setNewRegistry({...newRegistry, access: AccessOption.Private})
        }
        disableButtons={isFormDisabled}
      />
    </Tailwind>
  );
};
